import type { BoxDimensions } from '../parseBoxDimensions';
import {
  expireScanSessionIfStale,
  scanSessionPostSubmitStorageKey,
} from '../../../utils/scanSessionCache';

export const POST_SUBMIT_STEP_KINDS = {
  BOX_SELECT: 'box_select',
  BOX_SUGGESTION: 'box_suggestion',
  BOX_SCAN: 'box_scan',
  PACKING_PHOTO_RETAKE: 'packing_photo_retake',
} as const;

interface BoxSelectStep {
  readonly kind: typeof POST_SUBMIT_STEP_KINDS.BOX_SELECT;
}

interface BoxSuggestionStep {
  readonly kind: typeof POST_SUBMIT_STEP_KINDS.BOX_SUGGESTION;
  readonly dimensions: BoxDimensions;
}

interface BoxScanStep {
  readonly kind: typeof POST_SUBMIT_STEP_KINDS.BOX_SCAN;
  readonly retake: boolean;
  /** Read but not yet confirmed (confirm modal open). */
  readonly pendingDims: BoxDimensions | null;
}

interface PackingPhotoRetakeStep {
  readonly kind: typeof POST_SUBMIT_STEP_KINDS.PACKING_PHOTO_RETAKE;
}

/** Screen shown over the submitted nav bar. `null` means the nav bar itself. */
export type PostSubmitStep = BoxSelectStep | BoxSuggestionStep | BoxScanStep | PackingPhotoRetakeStep;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isBoxDimensions(value: unknown): value is BoxDimensions {
  return isRecord(value)
    && typeof value.length === 'number'
    && typeof value.width === 'number'
    && typeof value.height === 'number';
}

function parsePostSubmitStep(raw: string | null): PostSubmitStep | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) return null;
  switch (parsed.kind) {
    case POST_SUBMIT_STEP_KINDS.BOX_SELECT:
      return { kind: POST_SUBMIT_STEP_KINDS.BOX_SELECT };
    case POST_SUBMIT_STEP_KINDS.BOX_SUGGESTION:
      return isBoxDimensions(parsed.dimensions)
        ? { kind: POST_SUBMIT_STEP_KINDS.BOX_SUGGESTION, dimensions: parsed.dimensions }
        : null;
    case POST_SUBMIT_STEP_KINDS.BOX_SCAN:
      return {
        kind: POST_SUBMIT_STEP_KINDS.BOX_SCAN,
        retake: parsed.retake === true,
        pendingDims: isBoxDimensions(parsed.pendingDims) ? parsed.pendingDims : null,
      };
    case POST_SUBMIT_STEP_KINDS.PACKING_PHOTO_RETAKE:
      return { kind: POST_SUBMIT_STEP_KINDS.PACKING_PHOTO_RETAKE };
    default:
      return null;
  }
}

export function loadPostSubmitStep(orderId: string): PostSubmitStep | null {
  if (!orderId) return null;
  try {
    expireScanSessionIfStale();
    return parsePostSubmitStep(sessionStorage.getItem(scanSessionPostSubmitStorageKey(orderId)));
  } catch {
    return null;
  }
}

export function writePostSubmitStep(orderId: string, step: PostSubmitStep | null): void {
  if (!orderId) return;
  try {
    if (expireScanSessionIfStale()) return;
    const key = scanSessionPostSubmitStorageKey(orderId);
    if (step == null) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(step));
  } catch {
    // sessionStorage can be unavailable in private browsing.
  }
}
